import React from "react";

import SkeletonBlock from "../../common/SkeletonBlock";

function TableSkeletonRows({ cellClassNames, keyPrefix, rowCount = 5 }) {
  return Array.from({ length: rowCount }, (_, index) => (
    <tr
      key={`${keyPrefix}-${index}`}
      className="border-b border-greyscale-1"
      aria-hidden="true"
    >
      {cellClassNames.map((className, cellIndex) => (
        <td
          key={`${keyPrefix}-${index}-cell-${cellIndex}`}
          className={`px-3 py-3 ${cellIndex > 1 ? "text-center" : "text-left"}`}
        >
          <div className={cellIndex > 1 ? "flex justify-center" : ""}>
            <SkeletonBlock className={className} />
          </div>
        </td>
      ))}
    </tr>
  ));
}

export default function PatientProfileLoadingShell({ t }) {
  const measurementHeaderSkeletons = [
    "h-4 w-6 rounded-full",
    "h-4 w-28 rounded-full",
    "h-4 w-16 rounded-full",
    "h-4 w-14 rounded-full",
    "h-4 w-16 rounded-full",
    "h-4 w-16 rounded-full",
    "h-4 w-14 rounded-full",
  ];
  // Adherence + Profile, sized to their icon-over-label buttons.
  const headerActionSkeletons = ["min-h-[52px] w-28", "min-h-[52px] w-24"];
  const questionnaireHeaderSkeletons = [
    "h-4 w-6 rounded-full",
    "h-4 w-16 rounded-full",
    "h-4 w-14 rounded-full",
    "h-4 w-14 rounded-full",
    "h-4 w-14 rounded-full",
  ];

  return (
    <div
      className="flex h-full w-full flex-col gap-4 px-4 pb-4"
      aria-busy="true"
      data-testid="patient-profile-loading-shell"
    >
      <span className="sr-only">{t("profilePage.loading")}</span>

      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-col gap-2">
          <SkeletonBlock className="h-8 w-40 rounded-full" />
          <SkeletonBlock className="h-4 w-56 rounded-full" />
        </div>

        {/* Mirrors the loaded header: the metric cards and the overlay actions
            share one stretch row, so the buttons settle in place rather than
            shifting the counts sideways when the payload lands. */}
        <div className="flex flex-wrap items-stretch justify-end gap-2">
          <div className="grid grid-cols-2 gap-2">
            {Array.from({ length: 2 }, (_, index) => (
              <div
                key={`metric-card-skeleton-${index}`}
                className="card flex flex-col gap-2 px-3 py-2"
                aria-hidden="true"
              >
                <SkeletonBlock className="h-7 w-10 rounded-full" />
                <SkeletonBlock className="h-4 w-20 rounded-full" />
              </div>
            ))}
          </div>

          {headerActionSkeletons.map((className, index) => (
            <SkeletonBlock
              key={`header-action-skeleton-${index}`}
              className={`self-stretch rounded-xl ${className}`}
            />
          ))}
        </div>
      </div>

      <div className="grid min-h-0 flex-1 gap-4 lg:grid-cols-3">
        <section className="card flex min-h-[360px] flex-col overflow-hidden lg:col-span-2">
          <div className="flex flex-col gap-3 border-b border-greyscale-1 p-2 sm:flex-row sm:items-end sm:justify-between">
            <SkeletonBlock className="h-7 w-40 rounded-full" />
            <SkeletonBlock className="h-9 w-24 rounded-full" />
          </div>

          <div className="min-h-0 flex-1 overflow-auto bg-container">
            <table className="w-full min-w-[920px] table-fixed border-collapse">
              <colgroup>
                <col className="w-16" />
                <col className="w-[24%]" />
                <col className="w-[15%]" />
                <col className="w-[13%]" />
                <col className="w-[15%]" />
                <col className="w-[15%]" />
                <col className="w-[12%]" />
              </colgroup>

              <thead className="sticky top-0 z-10 bg-container shadow-[0_1px_0_0_var(--color-greyscale-1)]">
                <tr className="border-b border-greyscale-1">
                  {measurementHeaderSkeletons.map((className, index) => (
                    <th
                      key={`measurement-header-skeleton-${index}`}
                      scope="col"
                      className={`px-3 py-3 ${
                        index > 1 ? "text-center" : "text-left"
                      }`}
                    >
                      <div className={index > 1 ? "flex justify-center" : ""}>
                        <SkeletonBlock className={className} />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                <TableSkeletonRows
                  cellClassNames={[
                    "h-4 w-6 rounded-full",
                    "h-5 w-32 rounded-full",
                    "h-4 w-16 rounded-full",
                    "h-4 w-14 rounded-full",
                    "h-8 w-16 rounded-full",
                    "h-8 w-16 rounded-full",
                    "h-4 w-12 rounded-full",
                  ]}
                  keyPrefix="measurement-row-skeleton"
                />
              </tbody>
            </table>
          </div>
        </section>

        <section className="card flex min-h-[360px] flex-col overflow-hidden">
          <div className="border-b border-greyscale-1 patient-list-container">
            <SkeletonBlock className="h-6 w-36 rounded-full" />
          </div>

          <div className="min-h-0 flex-1 overflow-auto bg-container">
            <table className="w-full table-fixed border-collapse">
              <colgroup>
                <col className="w-16" />
                <col className="w-[30%]" />
                <col className="w-[22%]" />
                <col className="w-[22%]" />
                <col className="w-[18%]" />
              </colgroup>

              <thead className="sticky top-0 z-10 bg-container shadow-[0_1px_0_0_var(--color-greyscale-1)]">
                <tr className="border-b border-greyscale-1">
                  {questionnaireHeaderSkeletons.map((className, index) => (
                    <th
                      key={`questionnaire-header-skeleton-${index}`}
                      scope="col"
                      className={`patient-list-container ${
                        index > 1 ? "text-center" : "text-left"
                      }`}
                    >
                      <div className={index > 1 ? "flex justify-center" : ""}>
                        <SkeletonBlock className={className} />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                <TableSkeletonRows
                  cellClassNames={[
                    "h-4 w-6 rounded-full",
                    "h-5 w-20 rounded-full",
                    "h-4 w-16 rounded-full",
                    "h-4 w-14 rounded-full",
                    "h-4 w-10 rounded-full",
                  ]}
                  keyPrefix="questionnaire-row-skeleton"
                  rowCount={6}
                />
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
